/**
 * SB Vertical Video Campaigns
 * 9:16 video format for mobile stories, aligned with SbGroupKeywordCampaigns design
 */

import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Form, Button, Table, Alert, Spinner, Badge } from 'react-bootstrap';
import { FaRocket, FaCheckCircle, FaFileAlt, FaImage, FaTag, FaVideo, FaMobileAlt } from 'react-icons/fa';
import Layout from './Layout';
import SbVerticalVideoCampaignAPI from '../services/SbVerticalVideoCampaignAPI';
import '../assets/css/SbGroupKeywordCampaigns.css';

const SbVerticalVideoCampaigns = () => {
    // Files
    const [videoFile, setVideoFile] = useState(null);
    const [targetingFile, setTargetingFile] = useState(null);
    const [negativeFile, setNegativeFile] = useState(null);

    // Config
    const [targetingType, setTargetingType] = useState('keywords');
    const [productAsin, setProductAsin] = useState('');
    const [dailyBudget, setDailyBudget] = useState(15);
    const [defaultBid, setDefaultBid] = useState(1.0);
    const [campaignState, setCampaignState] = useState('paused');
    const [brandEntityId, setBrandEntityId] = useState('');
    const [isManualBrandEntry, setIsManualBrandEntry] = useState(false);
    const [matchType, setMatchType] = useState('BROAD');
    const [availableBrands, setAvailableBrands] = useState([]);
    const [campaignName, setCampaignName] = useState('');

    // Data
    const [targeting, setTargeting] = useState([]);
    const [negatives, setNegatives] = useState([]);

    // UI State
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [result, setResult] = useState(null);
    const [step, setStep] = useState(1);

    const getProfileId = () => localStorage.getItem('selectedAccountValue') || null;

    useEffect(() => {
        fetchBrands();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchBrands = async () => {
        const profileId = getProfileId();
        if (profileId) {
            try {
                const response = await SbVerticalVideoCampaignAPI.getBrands(profileId);
                if (response.success && response.brands) {
                    setAvailableBrands(response.brands);
                    if (response.brands.length > 0 && !brandEntityId) {
                        setBrandEntityId(response.brands[0].brandEntityId);
                    }
                }
            } catch (err) {
                console.error("Error fetching brands:", err);
            }
        }
    };

    // File Handlers
    const handleVideoChange = (e) => {
        const file = e.target.files[0];
        if (file && (file.type === 'video/mp4' || file.type === 'video/quicktime')) {
            if (file.size <= 500 * 1024 * 1024) {
                setVideoFile(file);
            } else {
                setError('The video must not exceed 500MB');
            }
        } else {
            setError('Only MP4 or MOV files are allowed');
        }
    };

    const handleTargetingFileChange = (e) => {
        const file = e.target.files[0];
        if (file && file.name.endsWith('.csv')) setTargetingFile(file);
    };

    const handleNegativeFileChange = (e) => {
        const file = e.target.files[0];
        if (file && file.name.endsWith('.csv')) setNegativeFile(file);
    };

    // Generate Preview
    const handleGeneratePreview = async () => {
        if (!brandEntityId) {
            setError('Please select a Brand Entity ID');
            return;
        }
        if (!videoFile) {
            setError('Please upload a vertical video (9:16)');
            return;
        }
        if (!targetingFile) {
            setError('Please upload the targeting file');
            return;
        }
        if (!productAsin.trim()) {
            setError('Please enter the product ASIN to promote');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const response = await SbVerticalVideoCampaignAPI.uploadFiles(videoFile, targetingFile, negativeFile);

            if (response.success) {
                const data = response.data;
                const parsedTargeting = targetingType === 'keywords' ? (data.keywords || []) : (data.products || []);

                setTargeting(parsedTargeting);
                if (data.negatives) setNegatives(data.negatives);

                const now = new Date();
                const dateStr = now.toISOString().slice(0, 10);
                const timeStr = now.toTimeString().slice(0, 8).replace(/:/g, '');
                setCampaignName(`SB_VV_${dateStr}_${timeStr}_${productAsin}`);

                setStep(2);
                setResult({ success: true, message: `Preview generated. ${parsedTargeting.length} targeting items loaded.` });
            } else {
                setError(response.error || 'Error processing files');
            }
        } catch (err) {
            setError(err.message || 'Error generating preview');
        } finally {
            setLoading(false);
        }
    };

    // Create Campaign
    const handleCreateCampaign = async () => {
        const profileId = getProfileId();
        if (!profileId) {
            setError('Please select an account');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const tomorrow = new Date();
            tomorrow.setDate(tomorrow.getDate() + 1);
            const startDateStr = tomorrow.toISOString().slice(0, 10);
            const endDate = new Date(tomorrow);
            endDate.setDate(endDate.getDate() + 30);
            const endDateStr = endDate.toISOString().slice(0, 10);

            const preparedTargeting = targeting.map(t => ({
                ...t,
                matchType: t.matchType || matchType,
                bid: t.bid || defaultBid
            }));

            const response = await SbVerticalVideoCampaignAPI.createCampaign({
                profileId,
                campaignName,
                dailyBudget,
                startDate: startDateStr,
                endDate: endDateStr,
                defaultBid,
                state: campaignState,
                brandEntityId,
                targetingType,
                targeting: preparedTargeting,
                productAsin,
                video: videoFile,
                negatives: negatives.length > 0 ? negatives : null
            });

            if (response.success) {
                setResult({
                    success: true,
                    message: 'Campaign created successfully',
                    campaignId: response.data.campaign.campaignId
                });
                setStep(3);
            } else {
                setError(response.message || 'Error creating campaign');
            }
        } catch (err) {
            setError(err.message || 'Error creating campaign');
        } finally {
            setLoading(false);
        }
    };

    const handleReset = () => {
        setVideoFile(null);
        setTargetingFile(null);
        setNegativeFile(null);
        setTargeting([]);
        setNegatives([]);
        setProductAsin('');
        setCampaignName('');
        setResult(null);
        setError(null);
        setStep(1);
    };

    return (
        <Layout>
            <Container fluid className="sb-keyword-campaigns-page p-4">
                {/* Header */}
                <Row className="mb-4">
                    <Col>
                        <h2><FaMobileAlt className="me-2" />SB Vertical Video Campaigns</h2>
                        <p className="text-muted">Vertical video (9:16) for mobile stories. 1 ASIN per campaign.</p>
                    </Col>
                </Row>

                {/* Alerts */}
                {error && <Alert variant="danger" onClose={() => setError(null)} dismissible>{error}</Alert>}
                {result && result.success && step < 3 && <Alert variant="success">{result.message}</Alert>}

                {/* Step 1: Setup */}
                {step === 1 && (
                    <>
                        {/* Config Section */}
                        <div className="config-section-card">
                            <Row className="g-3">
                                <Col md={4}>
                                    <label className="text-white mb-2 fw-bold">
                                        <FaTag className="config-label-icon" /> Brand Entity ID
                                    </label>
                                    <Form.Select
                                        className="custom-select" value={isManualBrandEntry ? 'MANUAL' : brandEntityId}
                                        onChange={e => {
                                            if (e.target.value === 'MANUAL') {
                                                setIsManualBrandEntry(true);
                                                setBrandEntityId('');
                                            } else {
                                                setIsManualBrandEntry(false);
                                                setBrandEntityId(e.target.value);
                                            }
                                        }}
                                    >
                                        <option value="">Select Brand...</option>
                                        {availableBrands.map((b, i) => (
                                            <option key={i} value={b.brandEntityId}>{b.brandEntityId} ({b.brandName || 'Unknown'})</option>
                                        ))}
                                        <option value="MANUAL">Enter manually...</option>
                                    </Form.Select>
                                    {isManualBrandEntry && (
                                        <Form.Control type="text" className="custom-input mt-2" placeholder="ENTITY..." value={brandEntityId} onChange={e => setBrandEntityId(e.target.value)} />
                                    )}
                                </Col>
                                <Col md={3}>
                                    <label className="text-white mb-2 fw-bold">ASIN to Promote</label>
                                    <Form.Control type="text" className="custom-input" placeholder="B0XXXXXXXX" value={productAsin} onChange={e => setProductAsin(e.target.value.toUpperCase())} maxLength={10} />
                                </Col>
                                <Col md={2}>
                                    <label className="text-white mb-2 fw-bold">Budget ($)</label>
                                    <Form.Control type="number" className="custom-input" value={dailyBudget} onChange={e => setDailyBudget(parseFloat(e.target.value))} min={1} />
                                </Col>
                                <Col md={2}>
                                    <label className="text-white mb-2 fw-bold">Bid ($)</label>
                                    <Form.Control type="number" className="custom-input" value={defaultBid} onChange={e => setDefaultBid(parseFloat(e.target.value))} min={0.02} step={0.01} />
                                </Col>
                            </Row>
                            <Row className="g-3 mt-2">
                                <Col md={3}>
                                    <label className="text-white mb-2 fw-bold">Targeting Type</label>
                                    <Form.Select className="custom-select" value={targetingType} onChange={e => setTargetingType(e.target.value)}>
                                        <option value="keywords">Keywords</option>
                                        <option value="products">Products (ASINs)</option>
                                    </Form.Select>
                                </Col>
                                {targetingType === 'keywords' && (
                                    <Col md={3}>
                                        <label className="text-white mb-2 fw-bold">Match Type</label>
                                        <Form.Select className="custom-select" value={matchType} onChange={e => setMatchType(e.target.value)}>
                                            <option value="BROAD">Broad</option>
                                            <option value="PHRASE">Phrase</option>
                                            <option value="EXACT">Exact</option>
                                        </Form.Select>
                                    </Col>
                                )}
                                <Col md={3}>
                                    <label className="text-white mb-2 fw-bold">Status</label>
                                    <Form.Select className="custom-select" value={campaignState} onChange={e => setCampaignState(e.target.value)}>
                                        <option value="paused">Paused</option>
                                        <option value="enabled">Enabled</option>
                                    </Form.Select>
                                </Col>
                            </Row>
                        </div>

                        {/* Upload Grid */}
                        <div className="upload-grid">
                            {/* Video */}
                            <div className={`upload-card-new ${videoFile ? 'has-file' : ''}`}>
                                <div className="card-required-badge">Required</div>
                                <div className="upload-icon"><FaVideo /></div>
                                <div className="upload-card-title">Vertical Video (9:16)</div>
                                <div className="upload-card-desc">MP4/MOV, 1080x1920px, 6-45 seg, max 500MB</div>
                                <label className="w-100 mt-auto">
                                    <div className="upload-btn">{videoFile ? 'Change Video' : 'Select Video'}</div>
                                    <input type="file" accept="video/mp4,video/quicktime" onChange={handleVideoChange} hidden />
                                </label>
                                {videoFile && <small className="text-success mt-2">{videoFile.name}</small>}
                            </div>

                            {/* Targeting CSV */}
                            <div className={`upload-card-new ${targetingFile ? 'has-file' : ''}`}>
                                <div className="card-required-badge">Required</div>
                                <div className="upload-icon"><FaFileAlt /></div>
                                <div className="upload-card-title">{targetingType === 'keywords' ? 'Keywords CSV' : 'Target ASINs CSV'}</div>
                                <div className="upload-card-desc">{targetingType === 'keywords' ? "Keyword, Match Type, Bid" : "ASINs where the ad will appear"}</div>
                                <label className="w-100 mt-auto">
                                    <div className="upload-btn">{targetingFile ? 'Change File' : 'Select File'}</div>
                                    <input type="file" accept=".csv" onChange={handleTargetingFileChange} hidden />
                                </label>
                                {targetingFile && <small className="text-success mt-2">{targetingFile.name}</small>}
                            </div>

                            {/* Negative Targeting */}
                            <div className={`upload-card-new ${negativeFile ? 'has-file' : ''}`}>
                                <div className="upload-icon"><FaFileAlt /></div>
                                <div className="upload-card-title">Negative Targeting (Optional)</div>
                                <div className="upload-card-desc">Keywords or ASINs to exclude</div>
                                <label className="w-100 mt-auto">
                                    <div className="upload-btn">{negativeFile ? 'Change File' : 'Select File'}</div>
                                    <input type="file" accept=".csv" onChange={handleNegativeFileChange} hidden />
                                </label>
                                {negativeFile && <small className="text-success mt-2">{negativeFile.name}</small>}
                            </div>
                        </div>

                        {/* Generate Button */}
                        <div className="generate-section">
                            <button className={`btn-generate-campaigns ${!loading ? 'ready' : ''}`} onClick={handleGeneratePreview} disabled={loading}>
                                {loading ? <><Spinner size="sm" animation="border" className="me-2" /> Processing...</> : <><FaRocket /> Generate Preview</>}
                            </button>
                        </div>

                        {/* Requirements */}
                        <div className="requirements-section">
                            <span className="requirements-title">Requirements - Vertical Video (9:16)</span>
                            <div className="req-item"><strong>Video:</strong> 9:16 format (1080x1920), 6-45 seconds, MP4/MOV, max 500MB.</div>
                            <div className="req-item"><strong>Product:</strong> 1 ASIN per campaign (optimized for mobile).</div>
                            <div className="req-item"><strong>Targeting:</strong> Keywords or Products, not both.</div>
                            <div className="req-item"><strong>Suggested budget:</strong> $15/day, Bid: $1.00.</div>
                        </div>
                    </>
                )}

                {/* Step 2: Preview */}
                {step === 2 && (
                    <Card className="config-section-card">
                        <Card.Header className="bg-dark text-white">
                            <div className="d-flex justify-content-between align-items-center">
                                <h5><FaRocket className="me-2" />Campaign Preview</h5>
                                <Button variant="outline-danger" size="sm" onClick={handleReset}>Reset</Button>
                            </div>
                        </Card.Header>
                        <Card.Body>
                            <Row className="mb-4">
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label className="text-white">Campaign Name</Form.Label>
                                        <Form.Control type="text" className="custom-input" value={campaignName} onChange={e => setCampaignName(e.target.value)} />
                                    </Form.Group>
                                    <p><strong>ASIN:</strong> {productAsin}</p>
                                    <p><strong>Video:</strong> {videoFile.name}</p>
                                    <p><strong>Budget:</strong> ${dailyBudget} | <strong>Bid:</strong> ${defaultBid}</p>
                                    <p><strong>Targeting:</strong> {targeting.length} {targetingType === 'keywords' ? 'keywords' : 'products'}</p>
                                </Col>
                                <Col md={6}>
                                    {targeting.length > 0 && (
                                        <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                                            <Table hover variant="dark" size="sm">
                                                <thead><tr><th>{targetingType === 'keywords' ? 'Keyword' : 'ASIN'}</th><th>Bid</th></tr></thead>
                                                <tbody>
                                                    {targeting.slice(0, 10).map((t, i) => (
                                                        <tr key={i}>
                                                            <td>{t.keywordText || t.keyword || t.asin || t.ASIN}</td>
                                                            <td>${t.bid || defaultBid}</td>
                                                        </tr>
                                                    ))}
                                                    {targeting.length > 10 && <tr><td colSpan={2} className="text-center text-muted">... and {targeting.length - 10} more</td></tr>}
                                                </tbody>
                                            </Table>
                                        </div>
                                    )}
                                </Col>
                            </Row>
                            <div className="text-center">
                                <Button variant="success" size="lg" onClick={handleCreateCampaign} disabled={loading} className="fw-bold">
                                    {loading ? <><Spinner size="sm" animation="border" className="me-2" /> Creating...</> : <><FaCheckCircle className="me-2" /> Create in Amazon</>}
                                </Button>
                            </div>
                        </Card.Body>
                    </Card>
                )}

                {/* Step 3: Results */}
                {step === 3 && result && (
                    <Card className="bg-dark text-white border-0">
                        <Card.Header className="bg-success text-white">
                            <FaCheckCircle className="me-2" /> Campaign Created
                        </Card.Header>
                        <Card.Body className="text-center">
                            <h4 className="mb-4">{result.message}</h4>
                            {result.campaignId && <p>Campaign ID: <Badge bg="primary">{result.campaignId}</Badge></p>}
                            <Button variant="primary" onClick={handleReset}>Create Another Campaign</Button>
                        </Card.Body>
                    </Card>
                )}
            </Container>
        </Layout>
    );
};

export default SbVerticalVideoCampaigns;

